// Selecting an exercise's past sessions. Pure (no Dexie), so the gym-scoping rule
// that everything else depends on is testable on its own; db/repo.ts is a thin
// wrapper that just supplies the sessions.

import type { GymId, Session } from './types';
import type { ExerciseHistoryPoint } from './progression';

export interface HistoryOpts {
  limit?: number;
  excludeSessionId?: string;
  // Restrict to one gym. Every planning path passes this: loads are only
  // comparable within a gym, so mixing them both mis-targets weight and makes the
  // 3-session stall detector fire on a gym change rather than a real plateau.
  // Omit it (trend charts, export) to get the full cross-gym history.
  gymId?: GymId | null;
}

// `sessions` must already be finished-only and newest-first.
export function selectExerciseHistory(
  sessions: Session[],
  exerciseId: string,
  opts: HistoryOpts = {},
): ExerciseHistoryPoint[] {
  const { limit = 12, excludeSessionId, gymId } = opts;
  const points: ExerciseHistoryPoint[] = [];
  for (const s of sessions) {
    if (s.id === excludeSessionId) continue;
    // No cross-gym fallback by design: the first session on an exercise at a new
    // gym has no history, says "log a baseline", and the weight gets typed once.
    // A borrowed number from the other gym would look authoritative and be wrong.
    if (gymId != null && s.gymId !== gymId) continue;
    const entry = s.entries.find((e) => e.exerciseId === exerciseId);
    if (!entry) continue;
    points.push({
      date: s.date, sets: entry.sets, bodyweightKg: s.bodyweightKg, note: entry.note, gymId: s.gymId,
    });
    if (points.length >= limit) break;
  }
  return points;
}
